'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Home,
  ListMusic,
  MoreHorizontal,
  Youtube,
} from 'lucide-react';
import { Z_INDEX } from '@/lib/constants/zIndex';
import { useChannelContext, makeChannelHref } from '@/lib/hooks/useChannelContext';
import { useSidebarStore } from '@/lib/stores/uiStore';
import { cn } from '@/lib/utils';

interface TabItem {
  href?: string;
  label: string;
  icon: React.ReactNode;
  isAction?: boolean;
}

interface MobileTabBarProps {
  className?: string;
}

export function MobileTabBar({ className }: MobileTabBarProps) {
  const pathname = usePathname();
  const { toggleMobileMore } = useSidebarStore();
  const { currentChannelId } = useChannelContext();

  // 관리자 운영 메뉴는 /admin 페이지로 통합. 모바일 탭바는 관리자/일반 동일.
  const TAB_ITEMS: TabItem[] = [
    {
      // 채널 없으면 /channels(empty state)로. /channels는 단채널 사용자를 채널
      // 상세로 자동 redirect, 다채널은 목록 표시. 신규 사용자는 빈 상태에서
      // "새 채널 만들기"를 자연스럽게 안내받음.
      href: currentChannelId ? `/channels/${currentChannelId}` : '/channels',
      label: '홈',
      icon: <Home className="w-5 h-5" />,
    },
    {
      href: makeChannelHref('/playlists', currentChannelId),
      label: '플레이리스트',
      icon: <ListMusic className="w-5 h-5" />,
    },
    {
      href: '/channels',
      label: '채널',
      icon: <Youtube className="w-5 h-5" />,
    },
    {
      label: '더보기',
      icon: <MoreHorizontal className="w-5 h-5" />,
      isAction: true,
    },
  ];

  const isActive = (href?: string) => {
    if (!href) {return false;}
    const cleanHref = href.split('?')[0];
    if (cleanHref === '/dashboard' || cleanHref === '/channels/create') {
      return pathname === cleanHref || pathname === '/';
    }
    // 채널 홈 활성화: /channels/{id}
    if (currentChannelId && cleanHref === `/channels/${currentChannelId}`) {
      return pathname === cleanHref;
    }
    return pathname === cleanHref || pathname?.startsWith(cleanHref + '/');
  };

  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200',
        'safe-area-inset-bottom',
        className
      )}
      style={{ zIndex: Z_INDEX.mobileTabBar }}
      role="tablist"
      aria-label="메인 탭"
    >
      <div className="flex items-stretch h-14">
        {TAB_ITEMS.map((item) => {
          const active = isActive(item.href);

          if (item.isAction) {
            return (
              <button
                key={item.label}
                onClick={toggleMobileMore}
                className={cn(
                  'flex-1 flex flex-col items-center justify-center gap-0.5',
                  'text-neutral-500 hover:text-neutral-700 transition-colors'
                )}
                role="tab"
                aria-label={item.label}
              >
                {item.icon}
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            );
          }

          return (
            <Link
              key={item.href}
              href={item.href!}
              className={cn(
                'flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors',
                active ? 'text-purple-600' : 'text-neutral-500 hover:text-neutral-700'
              )}
              role="tab"
              aria-selected={active}
              aria-label={item.label}
            >
              <span className={active ? 'text-purple-600' : 'text-neutral-500'}>
                {item.icon}
              </span>
              <span
                className={cn(
                  'text-[10px] font-medium',
                  active ? 'text-purple-600' : 'text-neutral-500'
                )}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export default MobileTabBar;
